import { T } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system/text-button';
import { NoSSR } from '@flipster/react/misc/lifecycle/NoSSR';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import TradingPauseIcon from '@flipster/ui/assets/svgs/trading-pause.svg';
import { numberFormatter } from '@flipster/utils/num';

import { useAuth } from '../auth/state';
import { zendesk } from '../zendesk/url';
import { TradingPausedLayer } from './paused-layer';
import { useTradingPause } from './state';

interface Props {
  symbol: string;
}

export const TradingPauseNotice = ({ symbol }: Props) => {
  const { isCookieLoggedIn } = useAuth();
  const { isPaused, status, pauseDuration, untilPauseStart } = useTradingPause(symbol);

  if (status === 'None' || !isCookieLoggedIn) return null;
  if (isPaused) return <TradingPausedLayer symbol={symbol} />;

  return (
    <div
      className={flex({
        justify: 'center',
        align: 'flex-start',
        gap: 12,
        w: '100%',
        minH: 149,
        p: '12px 16px 16px',
        bg: 'legacy.gray.2',
      })}
    >
      <span>
        <TradingPauseIcon />
      </span>
      <div>
        <span className={css({ textStyle: 'Legacy/H6', color: 'legacy.yellow' })}>
          <T
            id={
              status === 'Scheduled'
                ? 'Assets.r250401.Trading_pause_scheduled'
                : 'Assets.r250401.Potential_trading_pause'
            }
          />
        </span>
        {status === 'Scheduled' && (
          <p className={css({ textStyle: 'Legacy/Number4', color: 'legacy.yellow' })}>
            <T
              id='Trade.r250401.Trading_pause_scheduled_notice'
              values={{
                countdown: numberFormatter(untilPauseStart),
                interval: numberFormatter(pauseDuration),
              }}
            />
          </p>
        )}
        <p className={css({ textStyle: 'Legacy/Body3', color: 'legacy.gray.d' })}>
          <T
            id={
              status === 'Scheduled'
                ? 'Trade.r250401.Trading_pause_scheduled_description'
                : 'Assets.r250401.Potential_trading_pause_description'
            }
            values={{ interval: numberFormatter(pauseDuration) }}
          />
        </p>
        <NoSSR>
          <TextButton
            color='neutral'
            size='small'
            as='a'
            href={zendesk.getUrl('TradingPause')}
            newTab
            label={<T id='Autogen_common.r251126.s_Learn_More' />}
          />
        </NoSSR>
      </div>
    </div>
  );
};
